import { photoSmsLink } from "../data/mock";
import React from "react";
import { Link } from "react-router-dom";
import { Check, ArrowRight, Camera } from "lucide-react";
import SEO from "../components/SEO";
import { SITE_URL } from "../lib/site";
import { PriceFactors } from "../components/home/PriceFactors";
import { WarrantySection } from "../components/home/WarrantySection";
import { FinalCta } from "../components/home/OfferReviews";
import { PhotoQuoteSection } from "../components/home/PhotoQuoteForm";
import { useContent } from "../lib/content";

const WG_SMS = photoSmsLink("Hi! I'd like a White Glove quote.");
const INCLUDED = ["Delivery", "Two-person crew", "Strong VersaBox Pro black in-wall box included", "Wires hidden", "Calibration", "Programming", "Teaching the remote", "Packaging hauled away", "30-day callback"];
const CITIES = ["Rocky Mount", "Tarboro", "Wilson", "Nashville", "Zebulon", "Raleigh"];

const schema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "White Glove TV Installation for 75 inch and larger TVs",
  serviceType: "TV installation",
  provider: { "@id": `${SITE_URL}/#business` },
  areaServed: CITIES.map((c) => ({ "@type": "City", name: `${c}, NC` })),
  description: "Delivery, two-person crew, in-wall box, hidden wires, calibration, programming, remote training, packaging haul-away, and a 30-day callback. Custom quote.",
};

export default function WhiteGloveInstall() {
  const photo = useContent().photos.whiteGlove;
  return (
    <main data-testid="white-glove-page">
      <SEO
        title="White Glove TV Installation for 75 Inch and Larger TVs | Rocky Mount, NC"
        description="White Glove install for 75 inch and bigger and high-end TVs: delivery, two-person crew, Strong VersaBox Pro in-wall box, wires hidden, calibration, programming, and a 30-day callback. Custom quote. Text (252) 545-0011."
        path="/white-glove-tv-installation"
        jsonLd={schema}
      />
      <section className="bg-[#06142e] text-white">
        <div className="max-w-[1280px] mx-auto px-5 lg:px-8 py-16 md:py-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div>
            <p className="text-[#3b9dff] font-semibold tracking-widest uppercase text-xs">White Glove install</p>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.05]">White Glove TV Installation for 75 Inch and Bigger TVs</h1>
            <p className="mt-6 text-lg text-slate-300 max-w-xl">For 75 inch and bigger and expensive TVs. Custom quote. TV and equipment quoted separately by text or call.</p>
            <p className="mt-3 text-sm text-slate-400">Serving {CITIES.slice(0, -1).join(", ")}, and Raleigh.</p>
            <a href={WG_SMS} data-testid="wg-page-request-btn" className="mt-10 inline-flex items-center gap-3 px-7 py-4 rounded-full bg-[#3b9dff] hover:bg-[#2b8ae8] text-white font-bold text-lg transition-colors">
              <Camera className="w-5 h-5" /> Request White Glove, (252) 545-0011
            </a>
          </div>
          <img src={photo.src} alt={photo.alt} data-testid="wg-hero-photo" className="w-full aspect-square object-cover rounded-3xl ring-1 ring-white/10" />
        </div>
      </section>
      <section className="section-pad bg-white">
        <div className="max-w-[1000px] mx-auto px-5 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#0a1f44]">What White Glove includes</h2>
          <ul className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {INCLUDED.map((t) => (
              <li key={t} className="flex gap-3 rounded-2xl border border-slate-200 p-5 text-slate-700"><Check className="w-5 h-5 mt-0.5 shrink-0 text-[#3b9dff]" />{t}</li>
            ))}
          </ul>
          <p className="mt-6 text-slate-600">Price: custom quote. TV and equipment quoted separately by text or call.{" "}
            <Link to="/" data-testid="wg-home-offer-link" className="inline-flex items-center gap-1 font-semibold text-[#3b9dff] hover:underline">See the offer <ArrowRight className="w-4 h-4" /></Link>
          </p>
        </div>
      </section>
      <PhotoQuoteSection source="white-glove" />
      <PriceFactors />
      <WarrantySection />
      <FinalCta />
    </main>
  );
}
